import React, { useCallback, useEffect, useRef, useState } from "react"
import { useTranslation } from "react-i18next"
import { useMeContext } from "../../model/user/provider"
import { useChatContext } from "@/app/providers/useChatContext"
import { CModalCore } from "@shared/ui"
import { chatQuestions } from "../../locales/chatQuestions"
import type { ChatCategory, MessageStatusData } from "./types"
import type { ChatConversation, ChatMessage } from "../../graphql/main/gql/graphql"

// Components
import { ChatHeader } from "./components/ChatHeader"
import { ChatInput } from "./components/ChatInput"
import { ConversationsList } from "./components/ConversationsList"
import { ChatMessageList } from "./components/ChatMessageList"

// Hooks
import { useChatConversations } from "./hooks/useChatConversations"
import { useChatMessages } from "./hooks/useChatMessages"

type ChatLang = keyof typeof chatQuestions

const pickChatLang = (i18nLang: string): ChatLang => {
  const code = (i18nLang.split("-")[0] ?? i18nLang).toLowerCase()
  return (code in chatQuestions ? code : "en") as ChatLang
}

export interface ChatWidgetProps {
  pageContext: {
    title: string
    description: string
    path: string
    hash: string
  }
}

const ChatWidgetForUser: React.FC<ChatWidgetProps> = ({ pageContext }) => {
  const { i18n } = useTranslation()
  const { me } = useMeContext()
  const { isOpen, closeChat } = useChatContext()
  const currentLang = pickChatLang(i18n.language)
  const uiText = chatQuestions[currentLang].ui
  const [showConversationsList, setShowConversationsList] = useState(false)
  const [selectedCategory, setSelectedCategory] = useState<string | null>(null)
  const [isCreatingChat, setIsCreatingChat] = useState(false)
  const messagesEndRef = useRef<HTMLDivElement>(null)
  const chatContainerRef = useRef<HTMLDivElement>(null)
  const previousFocusRef = useRef<HTMLElement | null>(null)

  const {
    selectedConversation,
    setSelectedConversation,
    conversationsData,
    conversationsLoading,
    refetchConversations,
    handleCreateConversation,
    handleDeleteConversation,
    deletingIds,
    sortConversations,
    getChatTitle,
  } = useChatConversations(isOpen, showConversationsList, me.isPro || me.isCoach)

  const {
    messageText,
    setMessageText,
    isSendingMessage,
    pendingMessage,
    pollingMessageId,
    messageStatus,
    isPollingComplete,
    handleSendMessage: sendMessage,
    handleCancelSendMessage,
  } = useChatMessages({ refetchConversations })

  // Random questions logic
  const getRandomQuestions = useCallback(
    (count = 5, category?: string) => {
      const lang = pickChatLang(i18n.language)
      const data = chatQuestions[lang]

      let questions: string[] = []

      const categoriesRecord = data.categories as Record<string, ChatCategory>
      const selected = category ? categoriesRecord[category] : undefined
      if (selected) {
        questions = selected.questions
      } else {
        questions = []
        Object.entries(data.categories).forEach(([key, cat]) => {
          if (key === "forCoaches" && !me.isCoach) {
            return
          }
          questions.push(...(cat as ChatCategory).questions)
        })
      }

      const requiredQuestion =
        lang === "ru" ? "Составь список продуктов на эту неделю" : "Create a grocery list for this week"

      if (!category && questions.includes(requiredQuestion)) {
        const otherQuestions = questions.filter((q: string) => q !== requiredQuestion)
        const shuffled = [...otherQuestions].sort(() => 0.5 - Math.random())
        const selected = shuffled.slice(0, count - 1)
        const randomPosition = Math.floor(Math.random() * count)
        selected.splice(randomPosition, 0, requiredQuestion)
        return selected
      }
      const shuffled = [...questions].sort(() => 0.5 - Math.random())
      return shuffled.slice(0, Math.min(count, questions.length))
    },
    [i18n.language, me.isCoach],
  )

  const [randomQuestions, setRandomQuestions] = useState(() => getRandomQuestions(5))

  // A fresh pick when the language or role changes — the two inputs
  // getRandomQuestions reads. Done during render (react.dev: "adjusting
  // state when a prop changes") rather than in an effect, so the old set
  // never reaches the screen first.
  const questionsKey = `${i18n.language}|${me.isCoach}`
  const [prevQuestionsKey, setPrevQuestionsKey] = useState(questionsKey)
  if (prevQuestionsKey !== questionsKey) {
    setPrevQuestionsKey(questionsKey)
    setRandomQuestions(getRandomQuestions(5))
  }

  // Focus management when chat opens/closes
  useEffect(() => {
    if (isOpen) {
      // Store the previously focused element
      previousFocusRef.current = document.activeElement as HTMLElement

      // Focus the chat container for accessibility
      setTimeout(() => {
        const inputElement = chatContainerRef.current?.querySelector<HTMLInputElement>('input[type="text"]')
        inputElement?.focus()
      }, 100)
    } else if (previousFocusRef.current) {
      // Restore focus to the previous element when closing
      previousFocusRef.current.focus()
      previousFocusRef.current = null
    }
  }, [isOpen])

  // Scroll to bottom
  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" })
  }

  useEffect(() => {
    scrollToBottom()
  }, [conversationsData, isSendingMessage])

  // Message sending wrapper
  const handleSendMessage = useCallback(
    async (conversationId?: string, textToSend?: string) => {
      const convId = conversationId || selectedConversation
      await sendMessage(convId, textToSend || null, pageContext)
    },
    [selectedConversation, sendMessage, pageContext],
  )

  // Handle new chat
  const handleNewChat = useCallback(() => {
    setSelectedConversation(null)
    setMessageText("")
    setSelectedCategory(null)
    setRandomQuestions(getRandomQuestions(5))
  }, [setSelectedConversation, setMessageText, getRandomQuestions])

  // Filter categories based on user role
  const getFilteredCategories = useCallback((): Record<string, ChatCategory> => {
    const categories = chatQuestions[currentLang].categories as Record<string, ChatCategory>
    if (me.isCoach) {
      return categories
    }
    // Filter out forCoaches category for non-coaches
    const { forCoaches, ...filteredCategories } = categories
    return filteredCategories
  }, [currentLang, me.isCoach])

  // Handle create and send
  const handleCreateAndSend = useCallback(
    async (text: string) => {
      setIsCreatingChat(true)
      try {
        const newConvId = await handleCreateConversation()
        if (newConvId) {
          await handleSendMessage(newConvId, text)
        }
      } finally {
        setIsCreatingChat(false)
      }
    },
    [handleCreateConversation, handleSendMessage],
  )

  if (!isOpen) return null

  // The assistant opens in the app's side panel — the same CModalCore the
  // ration editor uses — so a panel over the page always looks and closes the
  // same way. The mock draws it as a centred window; the app's own convention
  // wins here (docs/DESIGN_SYSTEM.md §4.7).
  return (
    <CModalCore
      isOpen
      onClose={closeChat}
      position="center-right"
      size="7xl"
      /* The mock's 980px panel, leaving the app rail's 42px visible so it reads
         as a window over the app rather than as a new screen. The cap has to be
         !important: CModalCore pins center-right panels to max-w-2xl. */
      modalClassName="md:!max-w-[min(61.25rem,calc(100vw-50px))]"
      isForm={false}
      headerVariant="plain"
      headerCustom={
        <ChatHeader
          showConversationsList={showConversationsList}
          setShowConversationsList={setShowConversationsList}
          uiText={uiText}
        />
      }
      /* The columns own their scrolling, and they run edge to edge — the
         panel's own body padding would inset them. */
      wrapClassNameBody="min-h-0 !overflow-y-hidden !px-0 !pt-0"
      modalBodyContent={
        <div
          ref={chatContainerRef}
          className="flex h-full min-h-0 flex-col pb-safe-bottom"
        >
          <div className="flex flex-1 overflow-hidden">
            <ConversationsList
              conversations={sortConversations(
                (conversationsData?.chatConversations.items ?? []) as ChatConversation[],
              )}
              selectedConversation={selectedConversation}
              onSelectConversation={(id) => {
                setSelectedConversation(id)
                setMessageText("")
              }}
              onDeleteConversation={handleDeleteConversation}
              onNewChat={handleNewChat}
              deletingIds={deletingIds}
              showConversationsList={showConversationsList}
              setShowConversationsList={setShowConversationsList}
              getChatTitle={getChatTitle}
              uiText={uiText}
            />

            <div className="flex-1 flex flex-col overflow-hidden">
              {/* 20/24/24 desktop, 14/14/18 phone (docs/design/AI Assistant.dc.html). */}
              <div className="min-h-0 flex-1 overflow-y-auto px-6 pt-5 pb-6 max-md:px-3.5 max-md:pt-3.5 max-md:pb-4.5">
                <ChatMessageList
                  messages={
                    (conversationsData?.chatConversations.items.find((conv) => conv.id === selectedConversation)
                      ?.messages ?? []) as ChatMessage[]
                  }
                  conversationsLoading={conversationsLoading}
                  selectedConversation={selectedConversation}
                  pendingMessage={pendingMessage}
                  isSendingMessage={isSendingMessage}
                  pollingMessageId={pollingMessageId}
                  messageStatus={messageStatus as MessageStatusData | undefined}
                  isPollingComplete={isPollingComplete}
                  onCancelSendMessage={handleCancelSendMessage}
                  onQuestionClick={async (question) => {
                    setIsCreatingChat(true)
                    try {
                      if (!selectedConversation) {
                        const newConvId = await handleCreateConversation()
                        if (newConvId) {
                          await handleSendMessage(newConvId, question)
                        }
                      } else {
                        await handleSendMessage(selectedConversation, question)
                      }
                    } finally {
                      setIsCreatingChat(false)
                    }
                  }}
                  selectedCategory={selectedCategory}
                  setSelectedCategory={setSelectedCategory}
                  randomQuestions={randomQuestions}
                  setRandomQuestions={setRandomQuestions}
                  getRandomQuestions={getRandomQuestions}
                  getFilteredCategories={getFilteredCategories}
                  uiText={uiText}
                  currentLang={currentLang}
                  messagesEndRef={messagesEndRef}
                />
              </div>

              <ChatInput
                messageText={messageText}
                setMessageText={setMessageText}
                onSendMessage={async () => {
                  if (!selectedConversation) {
                    await handleCreateAndSend(messageText)
                  } else {
                    await handleSendMessage()
                  }
                }}
                isSendingMessage={isSendingMessage}
                isCreatingChat={isCreatingChat}
                uiText={uiText}
              />
            </div>
          </div>
        </div>
      }
    />
  )
}

// The widget is keyed by the signed-in user. When the user changes under it, as
// a session swap in another tab does once `me` is read again, everything it
// holds for the previous user, the selected conversation and a message being
// followed, goes with the old instance instead of carrying over to the next.
const ChatWidget: React.FC<ChatWidgetProps> = (props) => {
  const { me } = useMeContext()

  return (
    <ChatWidgetForUser
      key={me.id}
      {...props}
    />
  )
}

export default ChatWidget
